import { useEffect, useState } from 'react';
import { calculateTotalNights, isPercentage } from '../Util/general';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from "uuid";
import axios from 'axios';

import taxesNames from "../Util/data/taxesNames.json";
//import moment from "moment/moment";
import constants from '../Util/constants';
import { dividerFor, AGENT_COMMISSION_RATE_NET, BP_DIVIDER, ZERO_COMMISSION_MIN_BASE, commissionOfTotal, commissionFromNet } from '../Util/pricingPolicy';

// Debug logging gate — the old per-card/per-day console.log spam measurably
// slowed  rendering with DevTools open. Flip to true when debugging pricing.
const DEBUG_LOG = false;
const dlog = (...args) => { if (DEBUG_LOG) console.log(...args); };


// Prevent spamming RU debug API calls on re-render/recalculation
const ruAvbPriceInFlightKeys = new Set();

const normalizeToArray = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

// cta/ctd flags are PERMISSIONS ('ON' = check-in/out ALLOWED) for the channels
// that gate on them (RU/G- via rusync, BP via the BookingPal calendar webhook).
// Missing/unknown values default to ALLOWED. Hoisted from the RU branch so the
// BP branch shares the exact same semantics — never invert these.
const normalizeOnOffFlag = (value) => {
  if (value === undefined || value === null) return true;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    if (v === 'off' || v === 'false' || v === '0' || v === 'no') return false;
    if (v === 'on' || v === 'true' || v === '1' || v === 'yes') return true;
    return true;
  }
  return true;
};

// BookingPal charge_frequency vocabulary → the four RU-style quantifiers.
const BP_QUANTIFIER_MAP = {
  PER_STAY: 'PER_STAY',
  PER_BOOKING: 'PER_STAY',
  ONE_TIME: 'PER_STAY',
  PER_NIGHT: 'PER_NIGHT',
  PER_DAY: 'PER_NIGHT',
  PER_GUEST: 'PER_GUEST',
  PER_PERSON: 'PER_GUEST',
  PER_GUEST_PER_NIGHT: 'PER_GUEST_PER_NIGHT',
  PER_PERSON_PER_NIGHT: 'PER_GUEST_PER_NIGHT',
};

// ─── BookingPal fee/tax math (pure, exported for unit tests) ─────────────────
// Applies data.taxes[] as normalized by the hub's BP policies webhook
// (VTHub api/controllers/bookingpalListings.js updateBPListingPolicies):
// entries are {kind:'fee'|'tax', units FIXED|PERCENTAGE, amount, quantifier,
// isMandatory, isTaxable, isIncluded, minGuestsExtra, min/maxStayLength,
// startDate, endDate, name, type}.
// Rules (KEEP IN LOCKSTEP with VTHub api/services/pricingEngine.js
// calculateBpPrice — the cluster quote and this page price must agree):
//   - fees apply only when mandatory (optional fees are booking add-ons);
//     entries without `kind` are treated as fees and applied (legacy safety)
//   - taxes with isIncluded are already inside the nightly price → skipped
//   - stay-length windows (min/maxStayLength) and date windows (start/endDate,
//     approximated against the CHECK-IN date) gate both kinds
//   - FIXED amounts are converted via convertFixed(amount) then multiplied by
//     the normalized quantifier; minGuestsExtra bills only guests beyond the
//     included count; PERCENTAGE fees are % of rent
//   - taxes are % of (rent + taxable fees)
// Returns { feesTotal, taxesTotal, items } — items are display rows
// ({amount, description, initial}) in the caller's taxesArray shape.
export function computeBpFeesTaxes({ taxes, rent, nights, guests, checkInIso, convertFixed }) {
  const list = Array.isArray(taxes) ? taxes : [];
  const items = [];
  const applies = (t) => {
    if (t?.minStayLength && nights < Number(t.minStayLength)) return false;
    if (t?.maxStayLength && nights > Number(t.maxStayLength)) return false;
    if (t?.startDate && checkInIso < String(t.startDate).slice(0, 10)) return false;
    if (t?.endDate && checkInIso > String(t.endDate).slice(0, 10)) return false;
    return true;
  };

  let feesTotal = 0;
  let taxableFeesTotal = 0;
  list.forEach((fee) => {
    if (fee?.kind === 'tax') return;
    if (fee?.isMandatory === false) return;
    if (!applies(fee)) return;

    let amount = 0;
    if (isPercentage(fee?.units)) {
      amount = (rent * (Number(fee?.amount) || 0)) / 100;
    } else {
      const converted = convertFixed(Number(fee?.amount) || 0);
      const extraThreshold = Number(fee?.minGuestsExtra);
      const billableGuests = Number.isFinite(extraThreshold) && extraThreshold > 0
        ? Math.max(0, guests - extraThreshold)
        : guests;
      const rawQ = String(fee?.quantifier || 'PER_STAY').toUpperCase();
      const q = BP_QUANTIFIER_MAP[rawQ];
      if (!q) console.warn(`[BP] unknown quantifier "${fee?.quantifier}" for ${fee?.name || fee?.type}; treating as PER_STAY`);
      switch (q || 'PER_STAY') {
        case 'PER_NIGHT': amount = converted * nights; break;
        case 'PER_GUEST': amount = converted * billableGuests; break;
        case 'PER_GUEST_PER_NIGHT': amount = converted * billableGuests * nights; break;
        case 'PER_STAY': default: amount = converted; break;
      }
    }
    if (!(amount > 0)) return;
    feesTotal += amount;
    if (fee?.isTaxable) taxableFeesTotal += amount;
    // BP fee types are absent from taxesNames.json — use the webhook name.
    items.push({
      amount,
      description: fee?.name || fee?.type || 'Fee',
      initial: /clean/i.test(fee?.name || fee?.type || '') ? 'CF' : 'TX',
    });
  });

  let taxesTotal = 0;
  list.forEach((tax) => {
    if (tax?.kind !== 'tax') return;
    if (tax?.isIncluded) return;
    if (!applies(tax)) return;
    const amount = ((rent + taxableFeesTotal) * (Number(tax?.amount) || 0)) / 100;
    if (!(amount > 0)) return;
    taxesTotal += amount;
    items.push({ amount, description: tax?.name || 'Tax', initial: 'TX' });
  });

  return { feesTotal, taxesTotal, items };
}

// ─── Hostaway quote cache + price lines (pure, exported for unit tests) ──────
// A cached Hostaway quote is refreshed after HW_QUOTE_TTL_MS, STALE-WHILE-REVALIDATE:
// an entry that has a total keeps it (and its lineItems) while the refresh runs, and a
// failed refresh keeps it too. The price must never fall back to the calendar estimate
// just because a quote aged — the Book button sends the displayed total as the
// reservation and Flywire charge. Only an entry that NEVER had a total is `pending`.
// Entries written before this change carry no `at` and are refreshed on first use.
export const HW_QUOTE_TTL_MS = 15 * 60 * 1000;
export const HW_QUOTE_RETRY_MS = 60 * 1000;

const parseHwQuoteEntry = (raw) => {
  if (!raw) return null;
  try { const e = JSON.parse(raw); return e && typeof e === 'object' ? e : {}; } catch (e) { return {}; }
};
const hasHwQuoteTotal = (e) => Boolean(e) && Number.isFinite(e.total) && e.total > 0;

/** What to do with the cache entry on this render: { fetch, write } (write = new raw value or null). */
export function planHwQuoteRefresh(raw, now = Date.now()) {
  const entry = parseHwQuoteEntry(raw);
  const pending = JSON.stringify({ pending: true, at: now });
  if (!entry) return { fetch: true, write: pending };
  if (hasHwQuoteTotal(entry)) {
    // A refresh already running, or one that failed recently: wait.
    if (entry.refreshingAt && now - entry.refreshingAt <= HW_QUOTE_RETRY_MS) return { fetch: false, write: null };
    const fresh = Number.isFinite(entry.at) && now - entry.at <= HW_QUOTE_TTL_MS;
    if (fresh) return { fetch: false, write: null };
    return { fetch: true, write: JSON.stringify({ ...entry, refreshingAt: now }) };
  }
  // Never quoted: pending/failed attempts retry after HW_QUOTE_RETRY_MS.
  if (Number.isFinite(entry.at) && now - entry.at <= HW_QUOTE_RETRY_MS) return { fetch: false, write: null };
  return { fetch: true, write: pending };
}

/** The entry to store when a quote attempt fails: keep an existing total, else mark failed. */
export function hwQuoteFailureEntry(raw, now = Date.now(), error) {
  const entry = parseHwQuoteEntry(raw);
  if (hasHwQuoteTotal(entry)) return JSON.stringify({ ...entry, refreshingAt: now, lastRefreshError: error || 'quote failed' });
  return JSON.stringify({ failed: true, at: now, ...(error ? { error } : {}) });
}

const round2 = (n) => Math.round(n * 100) / 100;

// Hostaway price lines for the reserve page, the PDF and the offer, in SELLING terms:
// every line is net / divider (the same +22% as the total — VT's own cut is never
// shown on its own), and the lines add up to the displayed total exactly.
//
// From the live quote's components when they reconcile with its total (within 5
// cents): each non-zero component becomes a line; the base-rate/accommodation line is
// "Accomodation Fare" (the key the UI labels "Accommodation fare"), negative
// components (discounts) fold into it, and the rounding difference against the
// rounded-up total also lands on it. Otherwise (no quote yet, or components that don't
// add up): one accommodation line from the calendar nights, and a "Fees and taxes"
// line for whatever the total holds beyond them.
export function buildHwPriceLines({ lineItems, netTotal, sellingTotal, divider, calendarRent }) {
  const toSelling = (net) => round2((Number(net) || 0) / (divider || 1));
  const total = Number(sellingTotal) || 0;
  if (!(total > 0)) return [];

  const items = (Array.isArray(lineItems) ? lineItems : [])
    .filter((li) => li && li.includedInTotal !== false && li.includedInTotal !== 0)
    .map((li) => ({ type: String(li.type || ''), name: String(li.name || li.type || 'Fee'), amount: Number(li.amount) || 0 }))
    .filter((li) => li.amount !== 0);
  const itemsNet = items.reduce((s, li) => s + li.amount, 0);

  if (items.length && Math.abs(itemsNet - (Number(netTotal) || 0)) <= 0.05) {
    const isTax = (li) => /tax/i.test(li.type) || /tax/i.test(li.name);
    // Which component is the accommodation: Hostaway's own TYPE first, then the name —
    // the largest match in each tier, never the first ("Extra guest rate" must not beat
    // "Base rate"), then the largest non-tax component. A tax is never accommodation.
    const candidates = items.map((li, i) => ({ li, i })).filter(({ li }) => li.amount > 0 && !isTax(li));
    const largest = (list) => list.reduce((best, c) => (!best || c.li.amount > best.li.amount ? c : best), null);
    const rent = largest(candidates.filter(({ li }) => /price|rent|accommodation|nightly|base/i.test(li.type)))
      || largest(candidates.filter(({ li }) => /base|rent|night|accommodation|rate/i.test(li.name)))
      || largest(candidates);
    if (rent) {
      const lines = [];
      items.forEach((li, i) => {
        // The accommodation line is derived below as total minus the other lines, so
        // skipping a negative component here is what folds a discount into it.
        if (i === rent.i || li.amount < 0) return;
        lines.push({
          amount: toSelling(li.amount),
          description: li.name,
          initial: isTax(li) ? 'TX' : (/clean/i.test(li.name) ? 'CF' : 'FE'),
        });
      });
      const others = lines.reduce((s, l) => s + l.amount, 0);
      const accommodation = round2(total - others);
      // A discount bigger than the stay would make accommodation zero or negative (the
      // page hides such a line, the PDF prints it): don't itemise, use the fallback.
      if (accommodation > 0) {
        return [{ amount: accommodation, description: 'Accomodation Fare', initial: 'AF' }, ...lines];
      }
    }
  }

  const rent = Math.min(toSelling(calendarRent), total);
  if (!(rent > 0)) return [{ amount: total, description: 'Accomodation Fare', initial: 'AF' }];
  const rest = round2(total - rent);
  return rest > 1
    ? [{ amount: rent, description: 'Accomodation Fare', initial: 'AF' }, { amount: rest, description: 'Fees and taxes', initial: 'FE' }]
    : [{ amount: total, description: 'Accomodation Fare', initial: 'AF' }];
}

function makeCalculations(props) {
  const { property, xdata, activeRatePlan, fullCalendar, dateFrom, dateTo, adults = 1, children = 0, currency = 'USD', reservation_id = 'xxxx' } = props
  const client = localStorage.getItem("selectedClient") ? JSON.parse(localStorage.getItem("selectedClient")) : {}
  let calculated = false;
dlog('RU INtegrateion check,', property, xdata, dateFrom, dateTo)
  let totalAF = 0;
  let totalAFRES = 0;
  let totalAmount = 0
  let security = 0
  let totalAmountRES = 0
  let totalTaxes = 0
  let totalTaxesRES = 0
  let exchangeRate = 1
  let externalPrice = null
  let propertyExchangeRate = 1
  let agencyCommission = 0
  let agencyCommissionRES = 0
  let taxesArray = [];
  let resPayload = {}
  let dailyRatesRES = [];
  let taxesArrayRES = [];
  let getPrices = {};
  let exchangeRates = JSON.parse(localStorage.getItem("exchange") || "[]")
  let accommodates = adults + children || 1;
  let accountId = property?.accountId;
  let RatePlanCode = activeRatePlan
  let propertyId = property?._id
  let isSH = propertyId ? propertyId.substring(0, 2) === 'sh' : false
  let propertyTitle = property?.title
  let todayDate = new Date()
  let propertyCurrency = property?.prices?.currency
  // ─── PRICING MODEL (Tom + Moriya, 2026-07-17) ────────────────────────────────
  // The price we show = net + markup. The reseller is shown ONLY their own 10% (as
  // agencyCommission below) and never our internal cut. Channel providers get the raw
  // NET quote at approval — see Reservations/EditReservation — never any of this.
  //
  //   | Account         | Markup | Reseller commission         | VT keeps    | divider |
  //   |-----------------|--------|-----------------------------|-------------|---------|
  //   | SHM (585a39db…) |  +10%  | 10% of the total shown      |     -       | 1/1.10  |
  //   | everything else |  +22%  | 10% of the total shown      | 9.8% of net | 1/1.22  |
  //
  // SHM rates already include SH's margin at origin, so VT adds only the reseller's
  // 10% there. Uniform across every channel and account otherwise; RUDH G- twin
  // accounts are NOT the SHM tier (SHM = the legacy Guesty accountId only).
  // The commission (10% of the total shown since Moriya's mail of 2 Nov 2026) is
  // commissionOfTotal(total), in every branch, SHM included.
  //
  // The rule itself lives in Util/pricingPolicy (the FE's single source, twin of VTHub
  // api/services/pricingPolicy.js) — do not re-derive a markup here.
  let divider = dividerFor(accountId);
  // Route the live quote to the listing's ORIGIN hub: SHub listings from the merged
  // search are stamped _source 'SHUB' (→ api.triangle.luxury), everything else is
  // VTHUB. Same routing loadfullcalendar uses; defaults to VTHUB when unstamped
  // (deep-links). Without this a SHub property would quote against VTHUB, which
  // doesn't hold it, and silently fall back to the local estimate.
  const quoteHub = (property?._source === 'SHUB' || xdata?._source === 'SHUB')
    ? constants.SHUB_SOURCE_URL
    : constants.SHUB_URL;
  // Part of every quote cache key below: G-/RU-/BP- ids can sit on both hubs, and a
  // price cached for one hub's copy must not be reused for the other's.
  const hubTag = quoteHub === constants.SHUB_SOURCE_URL ? 'SH' : 'VT';
  // 10% of net: now only the "Prededucted Host Channel Fee" line of the reservation
  // (taxesArrayRES, which stays net). The agent's commission is commissionOfTotal.
  const COMMISSION_RATE_NET = AGENT_COMMISSION_RATE_NET;
  // ─── No-dates card: base price, with a calendar fallback ─────────────────────
  // Every "no dates selected" branch below shows data.prices.basePrice as the
  // per-night "from" price. That field is a synced SUMMARY and is sometimes stale or
  // junk (real cases: 1 USD and 0.05 USD on villas whose calendars are a healthy
  // 840-1735/night), which renders a $0 price and/or $0 agent commission on the card.
  // Dated quotes never use it — they sum fullCalendar nights — so the listing itself
  // is fine; only this one display path is wrong.
  //
  // So when basePrice is too low to render a non-zero commission, fall back to the
  // cheapest BOOKABLE future night. "MIN available nightly" is already the documented
  // semantic of this card (see the BP branch), and the value is in the property's own
  // currency, exactly like basePrice — so every caller's markup/conversion still applies.
  //
  // Three filters, each earning its place against real data:
  //   date >= today  — past nights are irrelevant to a "from" price.
  //   allotment > 0  — unbookable nights carry junk rates. Real case: a Bali villa whose
  //                    840-1050/night rows are all allotment 0, while its only bookable
  //                    nights are 0.045 placeholders.
  //   price >= MIN   — the night must itself render a non-zero commission, else we'd just
  //                    swap one $0 card for another. This is the SAME predicate the hubs'
  //                    getAllSearch guard tests with $elemMatch, so a listing that reaches
  //                    this code is guaranteed to have at least one qualifying night.
  const minFutureNightlyPrice = () => {
    if (!Array.isArray(fullCalendar) || !fullCalendar.length) return 0;
    const todayIso = dayjs().format('YYYY-MM-DD');
    let min = Infinity;
    for (const day of fullCalendar) {
      if (!day?.date) continue;
      if (String(day.date).slice(0, 10) < todayIso) continue;
      if (!(Number(day.allotment) > 0)) continue;
      const p = Number(day.price);
      if (Number.isFinite(p) && p >= ZERO_COMMISSION_MIN_BASE && p < min) min = p;
    }
    return min === Infinity ? 0 : min;
  };
  // Net per-night base for the no-dates card, in the property's currency.
  const noDatesBaseNightly = () => {
    const raw = Number(property?.prices?.basePrice) || 0;
    if (raw >= ZERO_COMMISSION_MIN_BASE) return raw;
    const fromCalendar = minFutureNightlyPrice();
    if (fromCalendar > 0) {
      dlog('[no-dates] basePrice', raw, 'unusable -> calendar min', fromCalendar);
      return fromCalendar;
    }
    return raw; // nothing better to show; the hubs keep these out of search anyway
  };

  let nights = dateFrom && dateTo ? calculateTotalNights(dateFrom, dateTo) : 0;
  let error = []
  let loadingPrice = isSH && dateFrom && dateTo && !localStorage.getItem("totalExPrice")

  function getExchangeRate(cur) {
    dlog('exchange rates',exchangeRates.find(
      (rate) => rate.currency_code === cur
    ))
    return exchangeRates.find(
      (rate) => rate.currency_code === cur
    )?.conversion_rates || 1;
  }
 
  function getTaxPerType(taxType) {
    return taxesNames.find(
      (tax) => tax.type === taxType
    ) || {};
  }

  function getEXprice() {
    if (!isSH || !loadingPrice) {
      dlog('waiting...')
    } else {
      dlog('getting Xprices:', propertyId)
      localStorage.removeItem("totalExPrice")
      // localStorage.setItem('expriceloading', true)
      let data = JSON.stringify({
        "dateFrom": dateFrom,
        "dateTo": dateTo,
        "currency": currency,
        "adults": adults,
        "children": children
      });

      let config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: constants.SHUB_URL+'/qoute/' + propertyId,
        headers: {
          'Authorization': 'Bearer __HUB_SESSION__',
          'Account-Id': '640625ea0620e40031b8597d',
          'Content-Type': 'application/json',
        },
        data: data
      };
      dlog('qoute:', config)
      // Unified quote (flag on): the SH adapter reuses this same qouteSH/SH-API
      // net. Adapt the NET DTO into the SH-API response shape so everything below
      // is untouched. Flag off = original POST /qoute path.
      const _uniSh = constants.USE_UNIFIED_QUOTE;
      const _shPromise = _uniSh
        ? axios.get(`${quoteHub}/api/booking/quote`, {
            params: {
              listingId: propertyId,
              checkIn: dayjs(dateFrom).format('YYYY-MM-DD'),
              checkOut: dayjs(dateTo).format('YYYY-MM-DD'),
              guests: (Number(adults) || 0) + (Number(children) || 0) || 1,
              currency,
            },
            headers: { 'x-api-key': constants.SHUB_API_KEY, Authorization: `Bearer ${constants.SHUB_STATIC_TOKEN}` },
          }).then((resp) => {
            const d = resp?.data || {};
            const shShaped = d.available
              ? { qouteResult: { success: true, available: true, prices: { priceInUSD: d.netTotal }, taxes: (d.lineItems || []).filter((li) => li.initial !== 'AF') } }
              : { qouteResult: { success: false, available: false, error: [d.error || 'dates not available'] } };
            return { data: { qouteResult: shShaped } };
          })
        : axios.request(config);
      _shPromise
        .then((response) => {
          dlog('exPrice:', response.data, JSON.stringify(response.data));
          externalPrice = response?.data?.qouteResult
          loadingPrice = false;
          const resSuccess = externalPrice?.qouteResult?.available
          if (externalPrice) {
            dlog('get XPrices: success, USD', externalPrice.qouteResult?.success, externalPrice.qouteResult.prices?.priceInUSD)
            if (externalPrice.qouteResult?.success) {
              totalAmount = parseInt(externalPrice.qouteResult.prices?.priceInUSD)
              totalTaxes = 0
              taxesArray = externalPrice?.qouteResult?.taxes
              dlog('total,success:', totalAmount, resSuccess)
              localStorage.setItem('totalExPrice', totalAmount)
              localStorage.setItem('EXtaxesArray', JSON.stringify(taxesArray))
              localStorage.removeItem('EXPriceError')
              if(typeof props.setTotalExPrice !== 'undefined') {
                props.setTotalExPrice(totalAmount)
              }
            }
            if (externalPrice?.qouteResult?.error) {
              error.push(externalPrice?.qouteResult?.error[0])
              localStorage.setItem('totalExPrice', 0)
              localStorage.setItem('EXtaxesArray', JSON.stringify([]))
              localStorage.setItem('EXPriceError', externalPrice?.qouteResult?.error[0] || '')
              if(typeof props.setTotalExPrice !== 'undefined') {
                props.setTotalExPrice(0)
              }
              
            }
          }
          agencyCommission = commissionOfTotal(totalAmount);
          agencyCommissionRES = commissionFromNet(totalAmountRES, divider);
        
          const price = {
            loadingPrice,
            currency,
            exchangeRate,
            propertyExchangeRate,
            //reservation_id,
            totalAmount,
            totalTaxes,
            taxesArray,
            agencyCommission,
          }
        
          const success = (!error)
          dlog('price return:', price)
          return {
            loadingPrice,
            error,
            calculated,
            success,
            reservation_id,
            totalAmount,
            security,
            totalTaxes,
            taxesArray,
            agencyCommission,
            totalAmountRES,
            totalTaxesRES,
            taxesArrayRES,
            agencyCommissionRES,
            exchangeRate,
            propertyExchangeRate,
            resPayload
          }
        })
        .catch((errorRes) => {
          dlog('API error:', errorRes);
          error = errorRes[0]
        });
    }

    // return ('some error fetching')

  }


  const addToDailyRates = (day) => {
    //dlog(tax,taxesArray)
    dailyRatesRES.push(day)
  }
  const addToTaxes = (tax) => {
    //dlog(tax,taxesArray)
    taxesArray.push(tax)
  }
  const addToTaxesRES = (taxRES) => {
    //dlog(taxRES,taxesArrayRES)
    taxesArrayRES.push(taxRES)
    //setTaxesArrayRES([...taxesArrayRES,taxRES])
  }


  // start calculation
dlog('currency data',currency, property?.prices?.currency, property?.prices)
  exchangeRate = getExchangeRate(currency);

  propertyExchangeRate = getExchangeRate(property?.prices?.currency); //set property exchange rate

  // main multiplier for the selected currency. Dividing by `divider` (1/1.10 for SHM,
  // 1/1.22 otherwise — see Util/pricingPolicy) applies the VillaTracker markup
  // (net -> selling price). The `/ divider` was previously disabled by a `//` typo,
  // which under-priced every non-RU quote.
  const multiplier = (exchangeRate / propertyExchangeRate) / divider;
dlog('currency data ---001',multiplier, property?.prices, exchangeRate, propertyExchangeRate, divider)
 const isVillasInStBarth = xdata?.channel?.[0] === "VillasInStBarth";

 const isHostawayProperty = (propertyId || '').startsWith('HW-');
 const isRUAPIIntegrated =
   xdata?.channel?.[0] === "RU" ||
   (propertyId || '').startsWith('G-') ||
   (propertyId || '').startsWith('RU-');

// =================================================
// Hostaway branch — INDEPENDENT of RU. Hostaway listings sync the same
// per-day fullCalendar[d].price (24-month window from VTHub
// step_hwCalendar + step_hwBuildFullCalendar), but we never touch any
// /ru-* endpoint here. Pricing rules:
//   1) sum fullCalendar[d].price for each night in range (currency-aware)
//   2) apply percentage taxes from property.taxes (same shape as RU)
//   3) fire-and-store /hostaway-price-preview as Hostaway-side verification
//   4) if verified price is cached and differs from local sum > $0.01,
//      OVERRIDE the displayed total with the Hostaway value
// =================================================
if (isHostawayProperty) {
  // -- step 3: fire-and-store the Hostaway price-preview (verify-only) --
  if (propertyId && dateFrom && dateTo) {
    const guestsHW = (Number(adults) || 0) + (Number(children) || 0) || 1;
    const dF = dayjs(dateFrom).format('YYYY-MM-DD');
    const dT = dayjs(dateTo).format('YYYY-MM-DD');
    const cacheKey = `hwVerifiedPrice_${hubTag}_${propertyId}_${dF}_${dT}_${guestsHW}`;
    // The cached quote used to live forever in this browser, so a rate change on the
    // partner's side never reached a page that had been quoted once. It is now
    // refreshed after HW_QUOTE_TTL_MS — stale-while-revalidate: the old total stays in
    // the entry (step 4 below reads it in this same call) until the new quote lands,
    // so the price never drops to the calendar estimate while refreshing.
    const plan = planHwQuoteRefresh(localStorage.getItem(cacheKey));
    if (plan.write) localStorage.setItem(cacheKey, plan.write);
    if (plan.fetch) {
      // Unified quote (flag on) self-calls this exact /hostaway-price-preview
      // upstream, so netTotal === the legacy `total` by construction. Flag off =
      // original path untouched. Downstream override is unchanged either way.
      const _uni = constants.USE_UNIFIED_QUOTE;
      const _hwReq = _uni
        ? axios.get(`${quoteHub}/api/booking/quote`, {
            params: { listingId: propertyId, checkIn: dF, checkOut: dT, guests: guestsHW, currency: propertyCurrency || 'USD' },
            headers: { 'x-api-key': constants.SHUB_API_KEY, Authorization: `Bearer ${constants.SHUB_STATIC_TOKEN}` },
          })
        : axios.get(`${constants.SHUB_URL}/hostaway-price-preview/${propertyId}`, {
            params: { dateFrom: dF, dateTo: dT, numberOfGuests: guestsHW, currency: propertyCurrency || 'USD' },
            headers: { 'x-api-key': constants.SHUB_API_KEY, Authorization: `Bearer ${constants.SHUB_STATIC_TOKEN}` },
          });
      _hwReq.then((resp) => {
        const data = resp?.data || {};
        const ok = _uni ? data.available === true : data.success;
        const total = _uni ? data.netTotal : data.total;
        if (ok && Number.isFinite(total)) {
          // Keep Hostaway's components (base rate, cleaning, fees, taxes) with the total:
          // they are the price breakdown. Unified quote: lineItems {type,name,amount};
          // legacy preview: components {type,title|name,total|amount}.
          const lineItems = _uni
            ? (Array.isArray(data.lineItems) ? data.lineItems : [])
            : (Array.isArray(data.components) ? data.components : []).map((c) => ({
                type: c?.type || 'FEE',
                name: c?.title || c?.name || c?.type || 'Component',
                amount: Number(c?.total ?? c?.amount) || 0,
                includedInTotal: c?.isIncludedInTotalPrice,
              }));
          localStorage.setItem(cacheKey, JSON.stringify({ total, currency: data.currency, lineItems, at: Date.now() }));
          dlog('[HW] verified price for', propertyId, dF, '→', dT, ':', total, data.currency);
        } else {
          localStorage.setItem(cacheKey, hwQuoteFailureEntry(localStorage.getItem(cacheKey)));
        }
        // The override that applies this verified price runs on the NEXT render, but
        // this async localStorage write alone does NOT re-render React — so the price
        // stayed stale ($0) until an unrelated re-render (e.g. a window resize). Force
        // one here, mirroring the RU branch's setRuApiPriceOverride.
        if (typeof props?.onPriceResolved === 'function') props.onPriceResolved();
      }).catch((err) => {
        localStorage.setItem(cacheKey, hwQuoteFailureEntry(localStorage.getItem(cacheKey), Date.now(), err?.message));
        if (typeof props?.onPriceResolved === 'function') props.onPriceResolved();
      });
    }
  }

  const hwReturn = () => ({
    loadingPrice, error, calculated, success: !error, reservation_id,
    totalAmount, security, totalTaxes, taxesArray, agencyCommission,
    totalAmountRES, totalTaxesRES, taxesArrayRES, agencyCommissionRES,
    exchangeRate, propertyExchangeRate, resPayload,
  });

  // No dates yet → show base price per night, no errors.
  if (property && (!dateFrom || !dateTo)) {
    localStorage.removeItem('bookingErrors');
    totalAmount = noDatesBaseNightly() * multiplier;
    calculated = true;
    agencyCommission = commissionOfTotal(totalAmount);
    agencyCommissionRES = commissionFromNet(totalAmountRES, divider);
    return hwReturn();
  }

  // Missing data → can't compute.
  if (!property || !dateFrom || !dateTo || !fullCalendar) {
    localStorage.setItem('bookingErrors', JSON.stringify([
      'Pricing/availability data is missing for the selected dates. Please change dates.'
    ]));
    calculated = true;
    totalAmount = 0; totalTaxes = 0; taxesArray = [];
    agencyCommission = 0;
    return hwReturn();
  }

  // -- step 1: sum nightly prices from fullCalendar --
  localStorage.removeItem('bookingErrors');
  let hwSum = 0;
  let cur = dayjs(dateFrom);
  const end = dayjs(dateTo);
  const targetCur = (currency || 'USD').toUpperCase();

  while (cur.isBefore(end, 'day')) {
    const formattedDate = cur.format('YYYY-MM-DD');
    const daysMatching = fullCalendar.filter(
      (period) => (period?.date || '').substring(0, 10) === formattedDate
    );
    if (!daysMatching || !daysMatching[0]) {
      localStorage.setItem('bookingErrors', JSON.stringify([
        'Pricing/availability data is missing for the selected dates. Please change dates.'
      ]));
      calculated = true;
      totalAmount = 0; totalTaxes = 0; taxesArray = [];
      agencyCommission = 0;
      return hwReturn();
    }
    const dayEntry = daysMatching[0];
    // Availability gate (the HW branch had none, unlike RU/BP): a blocked night
    // (allotment explicitly 0) can't be booked, so say so clearly instead of
    // pricing it or silently showing $0. Only gate on an explicit 0 — a missing
    // allotment stays bookable (don't false-block).
    if (dayEntry.allotment != null && !(Number(dayEntry.allotment) > 0)) {
      localStorage.setItem('bookingErrors', JSON.stringify([
        'Not available for the selected dates. Please choose different dates.'
      ]));
      calculated = true;
      totalAmount = 0; totalTaxes = 0; taxesArray = [];
      agencyCommission = 0;
      return hwReturn();
    }
    const dayPrice = Number(dayEntry.price) || 0;
    // Never silently sell a rate-less night at $0.
    if (!(dayPrice > 0)) {
      localStorage.setItem('bookingErrors', JSON.stringify([
        'Pricing is not available for the selected dates. Please change dates.'
      ]));
      calculated = true;
      totalAmount = 0; totalTaxes = 0; taxesArray = [];
      agencyCommission = 0;
      return hwReturn();
    }
    const dayCur = String(dayEntry.currency || targetCur).toUpperCase();
    let added = dayPrice;
    if (dayCur !== targetCur) {
      const tRate = getExchangeRate(targetCur);
      const dRate = getExchangeRate(dayCur) || 1;
      added = dayPrice * (tRate / dRate);
    }
    hwSum += added;
    addToDailyRates({
      price: dayPrice,
      EffectiveDate: formattedDate,
      ExpireDate: cur.add(1, 'day').format('YYYY-MM-DD'),
    });
    cur = cur.add(1, 'day');
  }

  // -- step 2: apply percentage taxes from property.taxes --
  taxesArray.length = 0;
  addToTaxes({ amount: hwSum, description: 'Accomodation Fare', initial: 'AF' });
  let hwTaxTotal = 0;
  const hwTaxes = Array.isArray(property?.taxes) ? property.taxes : [];
  hwTaxes.forEach((tax) => {
    if (!tax) return;
    if (isPercentage(tax?.units)) {
      const pct = Number(tax?.amount) || 0;
      const taxAmount = (hwSum * pct) / 100;
      hwTaxTotal += taxAmount;
      const meta = getTaxPerType(tax?.type) || {};
      addToTaxes({
        amount: taxAmount,
        description: meta.description || tax?.type || 'Tax',
        initial: meta.initial || 'TX',
      });
    }
  });

  totalTaxes = hwTaxTotal;
  totalAmount = hwSum + hwTaxTotal;

  // -- step 4: apply Hostaway verified-price override if cached --
  let hwQuoteLineItems = null;
  try {
    const guestsHW = (Number(adults) || 0) + (Number(children) || 0) || 1;
    const dF = dayjs(dateFrom).format('YYYY-MM-DD');
    const dT = dayjs(dateTo).format('YYYY-MM-DD');
    const cached = JSON.parse(localStorage.getItem(`hwVerifiedPrice_${hubTag}_${propertyId}_${dF}_${dT}_${guestsHW}`) || '{}');
    if (Number.isFinite(cached.total) && cached.total > 0) {
      if (Math.abs(cached.total - totalAmount) > 0.01) {
        dlog('[HW] override local', totalAmount, '→ Hostaway verified', cached.total);
        totalAmount = cached.total;
      }
      hwQuoteLineItems = Array.isArray(cached.lineItems) ? cached.lineItems : null;
    }
  } catch (e) { /* swallow */ }

  // The Hostaway net is final here (calendar sum, possibly replaced by their verified
  // total). Capture it BEFORE the markup: totalAmountRES is the reservation/channel
  // figure and must stay RAW NET (RU precedent) — Hostaway must never receive our
  // internal markup or the agent's commission.
  const hwNetTotal = Number(totalAmount) || 0;

  // net -> selling. This branch previously applied NO divider, so Hostaway stays were
  // displayed and sold at raw net (zero margin). The commission below is worked out on
  // the marked-up total, as in every branch.
  totalAmount = hwNetTotal / divider;

  // Round up to whole integer (mirror RU rounding rule)
  if (Number.isFinite(Number(totalAmount))) totalAmount = Math.ceil(Number(totalAmount));

  // The price lines, on the SAME basis as the total. The single line here used to be
  // the raw calendar sum (net, no markup, possibly an older sync) beside a marked-up
  // total built from Hostaway's live quote — and the quote's cleaning fee, other fees
  // and taxes were never listed. See buildHwPriceLines.
  const hwLines = buildHwPriceLines({
    lineItems: hwQuoteLineItems,
    netTotal: hwNetTotal,
    sellingTotal: Number(totalAmount) || 0,
    divider,
    calendarRent: hwSum,
  });
  taxesArray.length = 0;
  hwLines.forEach((line) => addToTaxes(line));

  totalAmountRES = hwNetTotal * (propertyExchangeRate / exchangeRate); // NET — never the marked-up total
  totalTaxesRES  = hwTaxTotal * (propertyExchangeRate / exchangeRate);
  security = 0;
  agencyCommission    = commissionOfTotal(totalAmount);
  agencyCommissionRES = commissionFromNet(totalAmountRES, divider);
  calculated = true;
  return hwReturn();
}

// =================================================
// BookingPal branch — native BP-<n> listings (NOT the sh-bp- Guesty relays,
// which stay on the generic path). Pricing rules:
//   1) hard-fail like RU (bookingErrors + zero total) on missing calendar
//      days, allotment 0, price 0, cta/ctd violations, and per-day minStay
//   2) sum fullCalendar[d].price per night (currency-aware)
//   3) fees/taxes from property.taxes via computeBpFeesTaxes (BP policies
//      webhook shape — keep in lockstep with VTHub calculateBpPrice)
//   4) hybrid verify: when props.bpLiveVerify is set (detail/reserve pages
//      only), fire-and-store /bookingpal-price-preview and override the NET
//      total on the next recalc when it differs > $0.01
//   5) display markup via BP_MARKUP_MODE below; totalAmountRES/totalTaxesRES
//      stay RAW net (RU precedent — the reservation stores the channel price)
// =================================================
if ((propertyId || '').startsWith('BP-')) {
  // ─── BP PRICING MARKUP — single place to change ───────────────────────────
  // 'divider' = standard VT net→selling markup. The value is PINNED (not accountId-
  // keyed like `divider` above): BP docs carry the property manager's own accountId,
  // so a BP listing whose PM happened to be SHM would otherwise mis-resolve to the
  // SHM tier. Sourced from Util/pricingPolicy (BP_DIVIDER), whose twin is VTHub
  // api/services/pricingPolicy.js (cluster quote and this page price must agree).
  // Alternates: 'factor' = flat × BP_MARKUP_FACTOR; 'none' = channel price as-is.
  const BP_MARKUP_MODE = 'divider';
  const BP_PINNED_DIVIDER = BP_DIVIDER; // pinned to the standard tier — see Util/pricingPolicy
  const BP_MARKUP_FACTOR = 1.07;
  const bpMarkup = (net) =>
    BP_MARKUP_MODE === 'divider' ? net / BP_PINNED_DIVIDER :
    BP_MARKUP_MODE === 'factor' ? net * BP_MARKUP_FACTOR : net;
  // ──────────────────────────────────────────────────────────────────────────

  const bpReturn = () => ({
    loadingPrice, error, calculated, success: !error, reservation_id,
    totalAmount, security, totalTaxes, taxesArray, agencyCommission,
    totalAmountRES, totalTaxesRES, taxesArrayRES, agencyCommissionRES,
    exchangeRate, propertyExchangeRate, resPayload,
  });
  const bpFail = (msgs) => {
    localStorage.setItem('bookingErrors', JSON.stringify(msgs));
    calculated = true;
    totalAmount = 0; totalTaxes = 0; taxesArray = [];
    agencyCommission = 0;
    return bpReturn();
  };
  const targetCur = (currency || 'USD').toUpperCase();
  const toDisplay = (amt, fromCur) => {
    const from = String(fromCur || propertyCurrency || targetCur).toUpperCase();
    if (from === targetCur) return amt;
    return amt * (getExchangeRate(targetCur) / (getExchangeRate(from) || 1));
  };

  // No dates yet → base price (MIN available nightly) per night, marked up.
  if (property && (!dateFrom || !dateTo)) {
    localStorage.removeItem('bookingErrors');
    totalAmount = bpMarkup(toDisplay(noDatesBaseNightly(), propertyCurrency));
    calculated = true;
    agencyCommission = commissionOfTotal(totalAmount);
    agencyCommissionRES = commissionFromNet(totalAmountRES, BP_PINNED_DIVIDER);
    return bpReturn();
  }

  if (!property || !fullCalendar) {
    return bpFail(['Pricing/availability data is missing for the selected dates. Please change dates.']);
  }

  // Same-day selection (nights 0) would skip every availability gate below yet
  // still pick up fixed PER_STAY fees — hub quoteListing rejects nights < 1,
  // so hard-fail here to stay in lockstep.
  if (!(nights >= 1)) {
    return bpFail(['Please select at least one night. Please change dates.']);
  }

  // cta/ctd permission gates — 'ON' = allowed (same convention the BP
  // calendar webhook writes; identical to the RU validation above).
  const findBpDay = (d) => {
    if (!d) return null;
    const dayIs = dayjs(d).format('YYYY-MM-DD');
    return fullCalendar.find((x) => (x?.date || '').substring(0, 10) === dayIs) || null;
  };
  const ctaOk = normalizeOnOffFlag(findBpDay(dateFrom)?.cta);
  const ctdOk = normalizeOnOffFlag(findBpDay(dateTo)?.ctd);
  if (!ctaOk || !ctdOk) {
    const msgs = [];
    if (!ctaOk) msgs.push('The dates selected does not allow check-in. Please change dates.');
    if (!ctdOk) msgs.push('The dates selected does not allow check-out. Please change dates.');
    return bpFail(msgs);
  }

  // Nightly loop: availability + per-day minStay + currency-aware sum.
  // Allotment is gated BEFORE price — BP nightly-mode booked days keep their
  // pushed nightly_price with allotment 0.
  localStorage.removeItem('bookingErrors');
  let bpSum = 0;
  let bpCur = dayjs(dateFrom);
  const bpEnd = dayjs(dateTo);
  while (bpCur.isBefore(bpEnd, 'day')) {
    const day = findBpDay(bpCur);
    if (!day) {
      return bpFail(['Pricing/availability data is missing for the selected dates. Please change dates.']);
    }
    if (!day.allotment || day.allotment <= 0) {
      return bpFail(['The dates selected are not available. Please change dates.']);
    }
    const dayMinStay = Number(day.minStay);
    if (Number.isFinite(dayMinStay) && nights < dayMinStay) {
      return bpFail([`This property can be booked for a minimum of ${dayMinStay} nights. Please change dates.`]);
    }
    const dayPrice = Number(day.price) || 0;
    if (dayPrice <= 0) {
      return bpFail(['The dates selected are not available. Please change dates.']);
    }
    const formattedDate = bpCur.format('YYYY-MM-DD');
    bpSum += toDisplay(dayPrice, day.currency);
    addToDailyRates({
      price: dayPrice,
      EffectiveDate: formattedDate,
      ExpireDate: bpCur.add(1, 'day').format('YYYY-MM-DD'),
    });
    bpCur = bpCur.add(1, 'day');
  }

  // Fees & taxes (shared pure helper — unit-tested, hub-parallel math).
  taxesArray.length = 0;
  addToTaxes({ amount: bpSum, description: 'Accomodation Fare', initial: 'AF' });
  const bpBreakdown = computeBpFeesTaxes({
    taxes: property?.taxes,
    rent: bpSum,
    nights,
    guests: accommodates,
    checkInIso: dayjs(dateFrom).format('YYYY-MM-DD'),
    convertFixed: (amt) => toDisplay(amt, propertyCurrency),
  });
  bpBreakdown.items.forEach(addToTaxes);
  totalTaxes = bpBreakdown.feesTotal + bpBreakdown.taxesTotal;
  let bpNetTotal = bpSum + totalTaxes;

  // Refundable security deposit: FX-convert only, never marked up.
  if (property?.prices?.securityDepositFee) {
    security = toDisplay(Number(property.prices.securityDepositFee) || 0, propertyCurrency);
  }

  // Hybrid live verify (detail/reserve pages only — props.bpLiveVerify).
  // Fire-and-store once per {id, dates, guests}; override the NET total on a
  // later recalc when BP's quote differs (catches unapplied price_rules and
  // the LOS nightly-sum approximation). Same localStorage pattern as the
  // Hostaway branch above.
  if (props.bpLiveVerify && propertyId && dateFrom && dateTo) {
    const dF = dayjs(dateFrom).format('YYYY-MM-DD');
    const dT = dayjs(dateTo).format('YYYY-MM-DD');
    const bpCacheKey = `bpVerifiedPrice_${hubTag}_${propertyId}_${dF}_${dT}_${accommodates}`;
    // TTLs: a resolved entry (success or failure) is refetched after 10 min so
    // BP repricing eventually shows; a 'pending' entry older than 30s is a
    // request that never resolved (navigation mid-flight) and may re-fire.
    const BP_VERIFY_TTL_MS = 10 * 60 * 1000;
    const BP_VERIFY_PENDING_MS = 30 * 1000;
    const nowMs = Date.now();
    let bpCached = null;
    try { bpCached = JSON.parse(localStorage.getItem(bpCacheKey) || 'null'); } catch (e) { bpCached = null; }
    const bpCacheStale = !bpCached
      || (bpCached.pending && nowMs - (bpCached.at || 0) > BP_VERIFY_PENDING_MS)
      || (!bpCached.pending && nowMs - (bpCached.at || 0) > BP_VERIFY_TTL_MS);
    if (bpCacheStale) {
      try {
        // Prune expired bpVerifiedPrice_ siblings so the keys don't accumulate
        // unbounded across property/date/guest combinations.
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const k = localStorage.key(i);
          if (!k || !k.startsWith('bpVerifiedPrice_') || k === bpCacheKey) continue;
          let v = null;
          try { v = JSON.parse(localStorage.getItem(k) || 'null'); } catch (e) { v = null; }
          if (!v || nowMs - (v.at || 0) > BP_VERIFY_TTL_MS) localStorage.removeItem(k);
        }
        localStorage.setItem(bpCacheKey, JSON.stringify({ pending: true, at: nowMs }));
      } catch (e) { /* quota — verify is best-effort, local price stands */ }
      // Unified quote (flag on) self-calls this exact /bookingpal-price-preview
      // upstream, so netTotal === the legacy `total` by construction. Flag off =
      // original path untouched. Downstream NET override is unchanged either way.
      const _uniBp = constants.USE_UNIFIED_QUOTE;
      const _bpReq = _uniBp
        ? axios.get(`${quoteHub}/api/booking/quote`, {
            params: { listingId: propertyId, checkIn: dF, checkOut: dT, guests: accommodates, currency: propertyCurrency || 'USD' },
            headers: { 'x-api-key': constants.SHUB_API_KEY, Authorization: `Bearer ${constants.SHUB_STATIC_TOKEN}` },
          })
        : axios.get(`${constants.SHUB_URL}/bookingpal-price-preview/${propertyId}`, {
            params: { dateFrom: dF, dateTo: dT, guests: accommodates },
            headers: { 'x-api-key': constants.SHUB_API_KEY, Authorization: `Bearer ${constants.SHUB_STATIC_TOKEN}` },
          });
      _bpReq.then((resp) => {
        const data = resp?.data || {};
        const ok = _uniBp ? data.available === true : data.success;
        const total = _uniBp ? data.netTotal : data.total;
        if (ok && Number.isFinite(total) && total > 0) {
          localStorage.setItem(bpCacheKey, JSON.stringify({ total, currency: data.currency, at: Date.now() }));
          dlog('[BP] verified price for', propertyId, dF, '→', dT, ':', total, data.currency);
        } else {
          localStorage.setItem(bpCacheKey, JSON.stringify({ failed: true, at: Date.now() }));
        }
        // Force a re-render so the override below applies without waiting for an
        // unrelated re-render (same fix as the Hostaway branch).
        if (typeof props?.onPriceResolved === 'function') props.onPriceResolved();
      }).catch((err) => {
        try { localStorage.setItem(bpCacheKey, JSON.stringify({ failed: true, at: Date.now(), error: err?.message })); } catch (e) { /* quota */ }
        if (typeof props?.onPriceResolved === 'function') props.onPriceResolved();
      });
    }
    if (bpCached && Number.isFinite(bpCached.total) && bpCached.total > 0) {
      const apiNet = toDisplay(bpCached.total, bpCached.currency);
      if (bpNetTotal > 0 && Math.abs(apiNet - bpNetTotal) > 0.01) {
        dlog('[BP] override local net', bpNetTotal, '→ BookingPal verified', apiNet);
        const verifyScale = apiNet / bpNetTotal;
        taxesArray = taxesArray.map((t) => ({ ...t, amount: (Number(t?.amount) || 0) * verifyScale }));
        totalTaxes = totalTaxes * verifyScale;
        bpNetTotal = apiNet;
      }
    }
  }

  // Pre-ceil the net BEFORE markup — the hub does ceil(net) in calculateBpPrice
  // and then ceil(net/divider) in applyPolicy (double ceil, same as the RU
  // branch's ordering below). A single ceil here diverged from the cluster
  // quote by $1 for any non-integer net.
  const bpNetCeiled = Math.ceil(bpNetTotal);

  // RAW net for the reservation (RU precedent: RES fields are never marked up).
  totalAmountRES = bpNetCeiled * (propertyExchangeRate / exchangeRate);
  totalTaxesRES = totalTaxes * (propertyExchangeRate / exchangeRate);

  // Display markup + ceil (hub parity: ceil(ceil(net)/BP_DIVIDER)), breakdown rows
  // scaled so the itemization still sums to the displayed total.
  if (bpNetTotal > 0) {
    const markedUpTotal = Math.ceil(bpMarkup(bpNetCeiled));
    const scale = markedUpTotal / bpNetTotal;
    taxesArray = taxesArray.map((t) => ({ ...t, amount: (Number(t?.amount) || 0) * scale }));
    totalTaxes = totalTaxes * scale;
    totalAmount = markedUpTotal;
  } else {
    totalAmount = 0;
  }

  calculated = true;
  agencyCommission = commissionOfTotal(totalAmount);
  agencyCommissionRES = commissionFromNet(totalAmountRES, BP_PINNED_DIVIDER);
  return bpReturn();
}

//  const nights = dateFrom && dateTo ? dayjs(dateTo).diff(dayjs(dateFrom), 'day') : 0;

if(isRUAPIIntegrated) {
  dlog('[RU] Entered RU branch', {
    propertyId: property?._id,
    dateFrom,
    dateTo,
    dateFromType: typeof dateFrom,
    dateToType: typeof dateTo,
    fullCalendarLen: Array.isArray(fullCalendar) ? fullCalendar.length : null,
    currency,
    exchangeRate,
    propertyCurrency: property?.prices?.currency,
    propertyExchangeRate,
    multiplier
  });

  // normalizeOnOffFlag hoisted to module scope (shared with the BP branch).

  const findCalendarDay = (d) => {
    if (!fullCalendar || !d) return null;
    const dayIs = dayjs(d).format('YYYY-MM-DD');
    const found = fullCalendar.find((x) => (x?.date || '').substring(0, 10) === dayIs) || null;
    dlog('[RU] findCalendarDay', { input: d, formatted: dayIs, found: Boolean(found), sample: found });
    return found;
  };

  // RU behavior:
  // - validate CTA on check-in date, CTD on check-out date
  // - if invalid -> set localStorage.bookingErrors and return (same return shape)
  // - if valid -> sum each stay-night day.price from fullCalendar (with currency conversion)

  // When no dates are selected, keep existing behavior: return base price per night.
  if (property && (!dateFrom || !dateTo)) {
    dlog('[RU] no dates selected -> base price per night', { basePrice: property?.prices?.basePrice, totalAmount });
    localStorage.removeItem('bookingErrors');
    totalAmount = noDatesBaseNightly() * multiplier;

    calculated = true;
    agencyCommission = commissionOfTotal(totalAmount);
    agencyCommissionRES = commissionFromNet(totalAmountRES, divider);

    const success = (!error);
    return {
      loadingPrice,
      error,
      calculated,
      success,
      reservation_id,
      totalAmount,
      security,
      totalTaxes,
      taxesArray,
      agencyCommission,
      totalAmountRES,
      totalTaxesRES,
      taxesArrayRES,
      agencyCommissionRES,
      exchangeRate,
      propertyExchangeRate,
      resPayload
    }
  }

  if (!property || !dateFrom || !dateTo || !fullCalendar) {
    dlog('[RU] missing required data', { hasProperty: Boolean(property), hasDateFrom: Boolean(dateFrom), hasDateTo: Boolean(dateTo), hasFullCalendar: Boolean(fullCalendar) });
    // Missing required data for RU calculation; don't crash the UI.
    localStorage.setItem('bookingErrors', JSON.stringify([
      'Pricing/availability data is missing for the selected dates. Please change dates.'
    ]));

    calculated = true;
    agencyCommission = commissionOfTotal(totalAmount);
    agencyCommissionRES = commissionFromNet(totalAmountRES, divider);
    const success = (!error);
    return {
      loadingPrice,
      error,
      calculated,
      success,
      reservation_id,
      totalAmount: 0,
      security,
      totalTaxes: 0,
      taxesArray: [],
      agencyCommission: 0,
      totalAmountRES,
      totalTaxesRES,
      taxesArrayRES,
      agencyCommissionRES,
      exchangeRate,
      propertyExchangeRate,
      resPayload
    }
  }

  const checkinDay = findCalendarDay(dateFrom);
  const checkoutDay = findCalendarDay(dateTo);

  // Degenerate-flag guard: the RU sync currently writes cta/ctd "OFF" on EVERY
  // day of whole calendars (rusync changeover mapping resolves everything to
  // "None"), which — read strictly — rejected every dated RU quote with "does
  // not allow check-in/out". A flag that is explicitly OFF on ALL days carries
  // no signal → ignore it. Feeds that mark only SOME days OFF (real changeover
  // rules) keep the gate. Mirror of the picker's rule in DatePickerComponent.jsx.
  const _explicitOff = (v) => v !== undefined && v !== null && !normalizeOnOffFlag(v);
  const ctaDegenerate = Array.isArray(fullCalendar) && fullCalendar.length > 0 && fullCalendar.every((d) => _explicitOff(d?.cta));
  const ctdDegenerate = Array.isArray(fullCalendar) && fullCalendar.length > 0 && fullCalendar.every((d) => _explicitOff(d?.ctd));

  const ctaOk = ctaDegenerate || normalizeOnOffFlag(checkinDay?.cta);
  const ctdOk = ctdDegenerate || normalizeOnOffFlag(checkoutDay?.ctd);

  dlog('[RU] CTA/CTD validation', {
    checkin: { dateFrom, formatted: dayjs(dateFrom).format('YYYY-MM-DD'), cta: checkinDay?.cta, ctaOk, day: checkinDay },
    checkout: { dateTo, formatted: dayjs(dateTo).format('YYYY-MM-DD'), ctd: checkoutDay?.ctd, ctdOk, day: checkoutDay }
  });

  if (!ctaOk || !ctdOk) {
    dlog('[RU] CTA/CTD invalid -> setting bookingErrors', { ctaOk, ctdOk });
    const msgs = [];
    if (!ctaOk) msgs.push('The dates selected does not allow check-in. Please change dates.');
    if (!ctdOk) msgs.push('The dates selected does not allow check-out. Please change dates.');
    localStorage.setItem('bookingErrors', JSON.stringify(msgs));

    calculated = true;
    const success = (!error);
    return {
      loadingPrice,
      error,
      calculated,
      success,
      reservation_id,
      totalAmount: 0,
      security,
      totalTaxes: 0,
      taxesArray: [],
      agencyCommission: 0,
      totalAmountRES,
      totalTaxesRES,
      taxesArrayRES,
      agencyCommissionRES,
      exchangeRate,
      propertyExchangeRate,
      resPayload
    }
  }

  // CTA/CTD valid -> clear errors and calculate by summing each stay-night
  dlog('[RU] CTA/CTD valid -> clearing bookingErrors and calculating nightly sum');
  localStorage.removeItem('bookingErrors');

  let notAvail = false
  let sum = 0;
  let currentDate = dayjs(dateFrom);
  const toDate = dayjs(dateTo);
  let requiredMinStay = 1;

  while (currentDate.isBefore(toDate, 'day')) {
    const formattedDate = currentDate.format('YYYY-MM-DD');
    const formattedNextDate = currentDate.add(1, 'day').format('YYYY-MM-DD');
    const selectedDay = fullCalendar ? fullCalendar
      .filter((period) => (period?.date || '').substring(0, 10) === formattedDate) : null

    dlog('[RU] Night loop day', { formattedDate, selectedDayLen: Array.isArray(selectedDay) ? selectedDay.length : null, sample: Array.isArray(selectedDay) ? selectedDay[0] : selectedDay });

    if (!selectedDay) {
      localStorage.setItem('bookingErrors', JSON.stringify([
        'Pricing/availability data is missing for the selected dates. Please change dates.'
      ]));
      calculated = true;
      const success = (!error);
      return {
        loadingPrice,
        error,
        calculated,
        success,
        reservation_id,
        totalAmount: 0,
        security,
        totalTaxes: 0,
        taxesArray: [],
        agencyCommission: 0,
        totalAmountRES,
        totalTaxesRES,
        taxesArrayRES,
        agencyCommissionRES,
        exchangeRate,
        propertyExchangeRate,
        resPayload
      }
    }

    // RU minStay validation: if any day in the selected range has a minStay higher than
    // selected nights, block booking.
    let dayMinStay = Number(selectedDay?.[0]?.minStay);
    if (!Number.isFinite(dayMinStay) || dayMinStay <= 0) dayMinStay = 1;
    if (dayMinStay > requiredMinStay) requiredMinStay = dayMinStay;
    dlog('[RU] minStay per day check', {
      formattedDate,
      dayMinStay,
      requiredMinStay,
      selectedNights: nights
    });

    if (requiredMinStay > 0 && nights > 0 && nights < requiredMinStay) {
      localStorage.setItem('bookingErrors', JSON.stringify([
        `This property can be booked for a minimum of ${requiredMinStay} nights. Please change dates.`
      ]));
      calculated = true;
      const success = (!error);
      return {
        loadingPrice,
        error,
        calculated,
        success,
        reservation_id,
        totalAmount: 0,
        security,
        totalTaxes: 0,
        taxesArray: [],
        agencyCommission: 0,
        totalAmountRES,
        totalTaxesRES,
        taxesArrayRES,
        agencyCommissionRES,
        exchangeRate,
        propertyExchangeRate,
        resPayload
      }
    }

    if (selectedDay && Array.isArray(selectedDay)) {
      if (!selectedDay[0]?.allotment) {
        notAvail = true
      }
    } else {
      notAvail = true
    }

    if (notAvail) {
      dlog('[RU] Not available (allotment false or invalid day object)', { formattedDate, sample: selectedDay && Array.isArray(selectedDay) ? selectedDay[0] : selectedDay });
      localStorage.setItem('bookingErrors', JSON.stringify([
        'The dates selected does not allow check-in or check-out. Please change dates.'
      ]));
      calculated = true;
      const success = (!error);
      return {
        loadingPrice,
        error,
        calculated,
        success,
        reservation_id,
        totalAmount: 0,
        security,
        totalTaxes: 0,
        taxesArray: [],
        agencyCommission: 0,
        totalAmountRES,
        totalTaxesRES,
        taxesArrayRES,
        agencyCommissionRES,
        exchangeRate,
        propertyExchangeRate,
        resPayload
      }
    }

    const selectedPrice = selectedDay.map((period) => period.price);
    const selectedPriceCurrency = selectedDay.map((period) => period.currency)[0];
    if (selectedPrice) {
      const targetCur = (currency || 'USD').toUpperCase();
      const dayCur = (selectedPriceCurrency || targetCur).toUpperCase();
      const dayPrice = Number(selectedPrice[0]) || 0;

      dlog('[RU] Day price', { formattedDate, dayPrice, dayCur, targetCur });

      // keep `sum` ALWAYS in `targetCur`
      const sumBefore = sum;
      let added = 0;
      if (dayCur === targetCur) {
        added = dayPrice;
      } else {
        const targetRate = getExchangeRate(targetCur);
        const dayRate = getExchangeRate(dayCur) || 1;
        const dayToTarget = targetRate / dayRate;
        added = dayPrice * dayToTarget;
      }
      sum += added;
      dlog('[RU] Day price added', {
        formattedDate,
        dayPrice,
        dayCur,
        targetCur,
        added,
        sumBefore,
        sumAfter: sum
      });

      addToDailyRates({
        price: dayPrice,
        EffectiveDate: formattedDate,
        ExpireDate: formattedNextDate
      });
    } else {
      localStorage.setItem('bookingErrors', JSON.stringify([
        'Pricing/availability data is missing for the selected dates. Please change dates.'
      ]));
      calculated = true;
      const success = (!error);
      return {
        loadingPrice,
        error,
        calculated,
        success,
        reservation_id,
        totalAmount: 0,
        security,
        totalTaxes: 0,
        taxesArray: [],
        agencyCommission: 0,
        totalAmountRES,
        totalTaxesRES,
        taxesArrayRES,
        agencyCommissionRES,
        exchangeRate,
        propertyExchangeRate,
        resPayload
      }
    }
    currentDate = currentDate.add(1, 'day');
  }

  // RU: for now, return ONLY the summed nightly rates (no extra-person fees, no cleaning fee,
  // no taxes/security deposit calculations). RU pricing rules will be handled separately.
  taxesArrayRES.length = 0
  taxesArray.length = 0

  addToTaxes({
    amount: sum,
    description: "Accomodation Fare",
    initial: "AF"
  })

  // RU/DH fee + tax rows (rusync extractRuFeesAndTaxes): PERCENTAGE rows are % of
  // rent; FIXED rows (cleaning, service, pet, per-guest tourist tax) are charged by
  // quantifier, converted from the property currency. The DH live quote — and the
  // hub's calculateRuPrice — charge all of them, so a card that skipped the FIXED rows
  // showed the stay without its cleaning fee (G- twins keep it ONLY here:
  // prices.cleaningFee is 0 on every one of them).
  let ruTaxTotal = 0;
  const ruTaxes = Array.isArray(property?.taxes) ? property.taxes : [];
  const ruNights = Math.max(1, dayjs(dateTo).diff(dayjs(dateFrom), 'day') || 1);
  const ruGuests = ((Number(adults) || 0) + (Number(children) || 0)) || 1;
  const ruTargetCur = (currency || 'USD').toUpperCase();
  const ruFeeCur = String(property?.prices?.currency || ruTargetCur).toUpperCase();
  const ruFeeToTarget = ruFeeCur === ruTargetCur ? 1 : (getExchangeRate(ruTargetCur) / (getExchangeRate(ruFeeCur) || 1));
  ruTaxes.forEach((tax) => {
    if (!tax) return;
    if (tax?.kind === 'tax' && tax?.isIncluded) return;
    if (Array.isArray(tax?.appliedOnFees) && tax?.isAppliedByDefault !== true) return; // dormant Guesty tax object
    let taxAmount = 0;
    if (isPercentage(tax?.units)) {
      const pct = Number(tax?.amount) || 0;
      taxAmount = (sum * pct) / 100;
    } else {
      if (tax?.isMandatory === false) return; // optional add-on
      const fixed = (Number(tax?.amount) || 0) * ruFeeToTarget;
      switch (BP_QUANTIFIER_MAP[String(tax?.quantifier || 'PER_STAY').toUpperCase()] || 'PER_STAY') {
        case 'PER_NIGHT': taxAmount = fixed * ruNights; break;
        case 'PER_GUEST': taxAmount = fixed * ruGuests; break;
        case 'PER_GUEST_PER_NIGHT': taxAmount = fixed * ruGuests * ruNights; break;
        default: taxAmount = fixed;
      }
    }
    if (!(taxAmount > 0)) return;
    ruTaxTotal += taxAmount;
    const meta = getTaxPerType(tax?.type) || {};
    addToTaxes({
      amount: taxAmount,
      description: meta.description || tax?.name || tax?.type || 'Tax',
      initial: meta.initial || (/clean/i.test(tax?.type || tax?.name || '') ? 'CF' : 'TX')
    });
  });

  totalAF = sum;
  totalAFRES = sum * (propertyExchangeRate / exchangeRate);
  totalTaxes = ruTaxTotal;
  totalAmount = sum + ruTaxTotal;
  totalTaxesRES = ruTaxTotal * (propertyExchangeRate / exchangeRate);
  totalAmountRES = totalAmount * (propertyExchangeRate / exchangeRate);
  security = 0;

  const parseRuAvbResponse = (data) => {
    const total = Number(
      data?.prices?.ReservationsBreakdowns?.ReservationBreakdown?.RUBreakdown?.Total ??
      data?.prices?.PropertyPrices?.PropertyPrice?._
    );
    const taxes = Number(data?.prices?.PropertyPrices?.PropertyPrice?.Taxes ?? NaN);
    const rent = Number(data?.prices?.ReservationsBreakdowns?.ReservationBreakdown?.RUBreakdown?.Rent ?? NaN);
    const securityDeposit = Number(data?.prices?.PropertyPrices?.PropertyPrice?.SecurityDeposit ?? NaN);
    const apiCurrency = (data?.prices?.PropertyPrices?.Currency || '').toUpperCase();

    const feeTaxItems = normalizeToArray(
      data?.prices?.ReservationsBreakdowns?.ReservationBreakdown?.RUBreakdown?.TotalFeesTaxes?.TotalFeeTax
    );

    const parsedFeeTaxes = feeTaxItems
      .map((t) => {
        const name = String(t?.Name || '').trim();
        const amount = Number(t?.Amount ?? 0);
        return {
          name,
          amount,
          alreadyIncluded: String(t?.AlreadyIncluded || '').toLowerCase() === 'true',
          feeTaxType: t?.FeeTaxType
        };
      })
      .filter((t) => Number.isFinite(t.amount));

    const feesSum = parsedFeeTaxes.reduce((acc, t) => acc + (Number(t.amount) || 0), 0);
    const afAmount = Number.isFinite(rent) ? rent : (Number.isFinite(total) ? (total - feesSum) : NaN);

    const taxesArrayFromApi = [];
    if (Number.isFinite(afAmount)) {
      taxesArrayFromApi.push({ amount: afAmount, description: 'Accomodation Fare', initial: 'AF' });
    }

    parsedFeeTaxes.forEach((t) => {
      const lower = (t.name || '').toLowerCase();
      const isCleaning = lower.includes('cleaning');
      taxesArrayFromApi.push({
        amount: t.amount,
        description: t.name || (isCleaning ? 'Cleaning Fee' : 'Tax'),
        initial: isCleaning ? 'CF' : 'TX'
      });
    });

    const taxesFallback = parsedFeeTaxes
      .filter((t) => !(String(t.name || '').toLowerCase().includes('cleaning')))
      .reduce((acc, t) => acc + (Number(t.amount) || 0), 0);

    return {
      status: Boolean(data?.status),
      total,
      taxes: Number.isFinite(taxes) ? taxes : taxesFallback,
      rent: afAmount,
      securityDeposit: Number.isFinite(securityDeposit) ? securityDeposit : 0,
      apiCurrency,
      taxesArray: taxesArrayFromApi
    };
  };

  // If the caller already has a fresh RU API result for this selection, and totals differ,
  // replace computed totals + taxesArray with API values.
  try {
    const guest = accommodates;
    const qDateFrom = dayjs(dateFrom).format('YYYY-MM-DD');
    const qDateTo = dayjs(dateTo).format('YYYY-MM-DD');
    const qCurrency = (currency || 'USD').toUpperCase();
    const avbKey = `${propertyId}|${qDateFrom}|${qDateTo}|${guest}|${qCurrency}`;

    const override = props?.ruApiPriceOverride;
    const hasMatchingOverride = override && override.key === avbKey && override.status === true;

    if (hasMatchingOverride) {
      const apiCur = String(override.currency || qCurrency || 'USD').toUpperCase();
      const displayCur = qCurrency;

      const convertMoney = (amount, fromCur, toCur) => {
        const n = Number(amount);
        if (!Number.isFinite(n)) return NaN;
        const src = String(fromCur || toCur || 'USD').toUpperCase();
        const dst = String(toCur || 'USD').toUpperCase();
        if (src === dst) return n;
        const dstRate = getExchangeRate(dst);
        const srcRate = getExchangeRate(src) || 1;
        return n * (dstRate / srcRate);
      };

      const apiTotal = convertMoney(override.total, apiCur, displayCur);
      const apiTaxes = convertMoney(override.taxes, apiCur, displayCur);
      const apiRent = convertMoney(override.rent, apiCur, displayCur);
      const apiSecurity = convertMoney(override.securityDeposit, apiCur, displayCur);
      const localTotal = Number(totalAmount);

      const differs = Number.isFinite(apiTotal) && Number.isFinite(localTotal) && Math.abs(apiTotal - localTotal) > 0.01;
      if (differs) {
        dlog('[RU] local total differs from API -> overriding totals', { localTotal, apiTotal, apiTaxes });
        totalAmount = apiTotal;
        totalTaxes = Number.isFinite(apiTaxes) ? apiTaxes : 0;
        security = Number.isFinite(apiSecurity) ? apiSecurity : 0;

        if (Array.isArray(override.taxesArray)) {
          taxesArray = override.taxesArray.map((t) => ({
            ...t,
            amount: convertMoney(t?.amount, apiCur, displayCur)
          }));
        }

        // Keep AF / sum aligned with API rent (in display currency).
        if (Number.isFinite(apiRent)) {
          sum = apiRent;
          totalAF = apiRent;
          totalAFRES = apiRent * (propertyExchangeRate / exchangeRate);
          const afIndex = Array.isArray(taxesArray)
            ? taxesArray.findIndex((t) => String(t?.initial || '').toUpperCase() === 'AF')
            : -1;
          if (afIndex >= 0) {
            taxesArray[afIndex] = { ...taxesArray[afIndex], amount: apiRent };
          }
        }

        totalAmountRES = totalAmount * (propertyExchangeRate / exchangeRate);
        totalTaxesRES = totalTaxes * (propertyExchangeRate / exchangeRate);
        agencyCommission = commissionOfTotal(totalAmount);
        agencyCommissionRES = commissionFromNet(totalAmountRES, divider);
      }
    }

    // No cache: fetch the API result and store it into caller state.
    // On next re-render, makeCalculations can apply the override if totals differ.
    const canStoreOverride = typeof props?.setRuApiPriceOverride === 'function';
    if (propertyId && canStoreOverride && !hasMatchingOverride && !ruAvbPriceInFlightKeys.has(avbKey)) {
      ruAvbPriceInFlightKeys.add(avbKey);
      // Unified endpoint — its RU adapter delegates to the SAME
      // /ru-getpropertyavbprice upstream and returns the channel-native payload as
      // `raw`, so parseRuAvbResponse below is unchanged (identical money math).
      const url = `${quoteHub}/api/booking/quote`;
      dlog('[RU] calling unified quote', {
        url,
        params: { listingId: propertyId, checkIn: qDateFrom, checkOut: qDateTo, guests: guest, currency: qCurrency }
      });

      const localTotalAtRequest = Number(totalAmount);
      const localTaxesAtRequest = Number(totalTaxes);

      axios.get(url, {
        params: {
          listingId: propertyId,
          checkIn: dayjs(qDateFrom).format('YYYY-MM-DD'),
          checkOut: dayjs(qDateTo).format('YYYY-MM-DD'),
          guests: guest,
          currency: qCurrency
        },
        headers: {
          'x-api-key': constants.SHUB_API_KEY, Authorization: `Bearer ${constants.SHUB_STATIC_TOKEN}`
        }
      }).then((response) => {
        dlog('[RU] unified quote response', response?.data);
        const parsed = parseRuAvbResponse(response?.data?.raw ?? response?.data);
        const apiTotal = Number(parsed.total);
        const differs = Number.isFinite(apiTotal) && Number.isFinite(localTotalAtRequest) && Math.abs(apiTotal - localTotalAtRequest) > 0.01;
        dlog('[RU] compare local vs API', {
          localTotal: localTotalAtRequest,
          apiTotal,
          localTaxes: localTaxesAtRequest,
          apiTaxes: parsed.taxes,
          differs
        });
        props.setRuApiPriceOverride({
          key: avbKey,
          status: parsed.status,
          total: parsed.total,
          taxes: parsed.taxes,
          rent: parsed.rent,
          securityDeposit: parsed.securityDeposit,
          currency: parsed.apiCurrency || qCurrency,
          taxesArray: parsed.taxesArray,
          receivedAt: Date.now()
        });
      }).catch((err) => {
        dlog('[RU] avb/price API error', err);
      }).finally(() => {
        ruAvbPriceInFlightKeys.delete(avbKey);
      });
    }
  } catch (e) {
    dlog('[RU] avb/price API call/override skipped (exception)', e);
  }

  dlog('[RU] final totals', {
    sum,
    totalAmount,
    totalTaxes,
    taxesArray,
    totalAmountRES,
    exchangeRate,
    propertyExchangeRate,
    bookingErrorsInStorage: localStorage.getItem('bookingErrors')
  });

  // RU requirement: always round TOTAL up to next highest integer.
  // Example: 563.41 -> 564
  {
    const rawTotal = Number(totalAmount);
    if (Number.isFinite(rawTotal)) {
      const roundedTotal = Math.ceil(rawTotal);
      const delta = roundedTotal - rawTotal;
      if (delta > 0) {
        // Keep breakdown consistent by applying the delta to Accommodation Fare.
        const afIndex = Array.isArray(taxesArray)
          ? taxesArray.findIndex((t) => String(t?.initial || '').toUpperCase() === 'AF')
          : -1;
        if (afIndex >= 0) {
          const afAmount = Number(taxesArray[afIndex]?.amount) || 0;
          taxesArray[afIndex] = { ...taxesArray[afIndex], amount: afAmount + delta };
        }
        totalAmount = roundedTotal;
        // keep RES totals consistent after rounding
        totalAmountRES = totalAmount * (propertyExchangeRate / exchangeRate);
        dlog('[RU] totalAmount rounded up (ceil)', { rawTotal, roundedTotal, delta });
      }
    }
  }

  // ── RU display markup vs raw reservation price ───────────────────────────────
  // RU gives us the real (net) RU price and we override with it above. That price is
  // UNDER-priced for display (no VillaTracker markup). So the DISPLAY total (page + PDF)
  // must show the selling price = raw / divider (the account's tier), while a RESERVATION
  // must send the RAW RU price. `totalAmountRES` / `taxesArrayRES` were already set from
  // the raw amount, so leave them untouched (raw) and mark up only the display fields:
  // totalAmount, its breakdown (taxesArray), totalTaxes — re-rounded up per the RU rule.
  if (divider && divider !== 1 && Number.isFinite(Number(totalAmount)) && Number(totalAmount) > 0) {
    const rawTotal = Number(totalAmount);
    const markedUpTotal = Math.ceil(rawTotal / divider);   // selling price, RU rounds up
    const scale = markedUpTotal / rawTotal;
    if (Array.isArray(taxesArray)) {
      taxesArray = taxesArray.map((t) => ({ ...t, amount: (Number(t?.amount) || 0) * scale }));
    }
    totalTaxes = Number(totalTaxes) * scale;
    totalAmount = markedUpTotal;
    // NOTE: totalAmountRES / totalTaxesRES / taxesArrayRES intentionally stay RAW (RU price)
    // so the reservation sends the actual RU quote, not the marked-up display price.
  }

  calculated = true
  agencyCommission = commissionOfTotal(totalAmount);
  agencyCommissionRES = commissionFromNet(totalAmountRES, divider);
  const success = (!error)
  return {
    loadingPrice,
    error,
    calculated,
    success,
    reservation_id,
    totalAmount,
    security,
    totalTaxes,
    taxesArray,
    agencyCommission,
    totalAmountRES,
    totalTaxesRES,
    taxesArrayRES,
    agencyCommissionRES,
    exchangeRate,
    propertyExchangeRate,
    resPayload
  }
}


if (isVillasInStBarth) {
    // VillasInStBarth pricing logic
    const baseWeeklyPrice = property?.prices?.basePrice || 0;
    const bedrooms = property?.bedrooms || parseInt(localStorage.getItem('bedrooms')) || 2; // Default to 2 bedrooms
    const rates = xdata?.villainstbarth?.rates || [];
    
    // If no dates are selected, show base weekly price
    if (!dateFrom || !dateTo) {
        return {
            // Marked up like every other channel (was raw net); commission as everywhere
            // (was VIB's contractual 12.5%) — see the model note at the top.
            totalAmount: (baseWeeklyPrice / divider) * exchangeRate,
            totalTaxes: 0,
            security: 0,
            agencyCommission: commissionOfTotal((baseWeeklyPrice / divider) * exchangeRate),
            loadingPrice: false,
            currency: currency || "USD",
            isVillasInStBarth: true
        };
    }
    
    // Helper function to find rate period for a specific date
    // const findRatePeriod = (date) => {
    //     const checkDate = dayjs(date);
    //     dlog('check date for rate finder', checkDate, date)
    //     return rates.find(rate => {
    //         const startDate = dayjs(rate.start);
    //         const endDate = dayjs(rate.end);
    //         // Use the correct dayjs comparison methods
    //         return checkDate.isSameOrAfter(startDate, 'day') && checkDate.isBefore(endDate, 'day');
    //     });
    // };
    const findRatePeriod = (date) => {
        const checkDate = dayjs(date);
        dlog('check date for rate finder', checkDate, date)
        return rates.find(rate => {
          const startDate = dayjs(rate.start);
          const endDate = dayjs(rate.end);
          // Use built-in dayjs methods without plugins
          return (checkDate.isAfter(startDate) || checkDate.isSame(startDate)) && 
            checkDate.isBefore(endDate);
        });
    };
    
    // Helper function to calculate price with commission
    const calculatePriceWithCommission = (weeklyPrice) => {
        // Commission formula: ((price * 12.5%) + price) / 0.9
        const priceWithCommission = weeklyPrice //((weeklyPrice * 0.125) + weeklyPrice);
        return priceWithCommission;
    };
    
    // Helper function to get daily price from weekly price
    const getDailyPrice = (weeklyPrice) => {
        const priceWithCommission = calculatePriceWithCommission(weeklyPrice);
        return priceWithCommission / 7; // Convert weekly to daily
    };
    
    // Calculate total price for the date range
    let totalPrice = 0;
    let currentDate = dayjs(dateFrom);
    const endDateObj = dayjs(dateTo);
    const totalDays = endDateObj.diff(currentDate, 'day');
    
    dlog('VillasInStBarth calculation:', {
        dateFrom: currentDate.format('YYYY-MM-DD'),
        dateTo: endDateObj.format('YYYY-MM-DD'),
        totalDays,
        bedrooms,
        rates: rates.length
    });
    
    // Calculate price for each day
    for (let dayIndex = 0; dayIndex < totalDays; dayIndex++) {
        const currentStayDate = currentDate.add(dayIndex, 'day');
        // Use the next day (checkout date) for rate lookup instead of current day
        const checkoutDate = currentStayDate.add(1, 'day');
        const ratePeriod = findRatePeriod(checkoutDate);
        
        
        if (ratePeriod) {
            // Get weekly price for the bedroom count
            const weeklyPrice = ratePeriod.prices[bedrooms.toString()] || ratePeriod.prices["2"];
            const dailyPrice = getDailyPrice(weeklyPrice);
            totalPrice += dailyPrice;
            
            dlog('Day price calculation:', {
                dayIndex: dayIndex + 1,
                date: currentStayDate.format('YYYY-MM-DD'),
                nextDate: currentStayDate.add(1, 'day').format('YYYY-MM-DD'),
                period: ratePeriod.name,
                weeklyPrice,
                dailyPrice
            });
        } else {
            // Fallback to base price if no rate period found
            const dailyPrice = getDailyPrice(baseWeeklyPrice);
            totalPrice += dailyPrice;
            
            dlog('Fallback price for day:', {
                dayIndex: dayIndex + 1,
                checkinDate: currentStayDate.format('YYYY-MM-DD'),
                checkoutDate: checkoutDate.format('YYYY-MM-DD'),
                rateLookupDate: checkoutDate.format('YYYY-MM-DD'),
                dailyPrice
            });
        }
    }
    
    // totalPrice is the VIB net. Apply the standard markup (our internal cut + the
    // agent's 10%) then convert: this branch previously shipped raw net as the display
    // price, i.e. VIB stays were sold with zero margin.
    let finalTotalAmount = (totalPrice / divider) * exchangeRate;
    let vibNet = totalPrice * exchangeRate; // net (display currency)

    // Route VIB through the ONE unified endpoint too. Fire-and-store; override with
    // the endpoint's NET when available. NOTE: the endpoint's VIB adapter prices from
    // fullCalendar while this branch uses xdata.villainstbarth.rates — if a listing's
    // calendar and rates differ, the endpoint value wins (worth a spot-check).
    const _vDF = dayjs(dateFrom).format('YYYY-MM-DD');
    const _vDT = dayjs(dateTo).format('YYYY-MM-DD');
    const _vKey = `uniVerifiedPrice_${hubTag}_${propertyId}_${_vDF}_${_vDT}`;
    if (propertyId && !localStorage.getItem(_vKey)) {
      localStorage.setItem(_vKey, JSON.stringify({ pending: true }));
      axios.get(`${quoteHub}/api/booking/quote`, {
        params: { listingId: propertyId, checkIn: _vDF, checkOut: _vDT, guests: 1, currency },
        headers: { 'x-api-key': constants.SHUB_API_KEY, Authorization: `Bearer ${constants.SHUB_STATIC_TOKEN}` },
      }).then((resp) => {
        const d = resp?.data || {};
        localStorage.setItem(_vKey, JSON.stringify(d.available && Number.isFinite(d.netTotal) ? { total: d.netTotal, currency: d.currency } : { failed: true }));
        if (typeof props?.onPriceResolved === 'function') props.onPriceResolved();
      }).catch(() => {
        localStorage.setItem(_vKey, JSON.stringify({ failed: true }));
        if (typeof props?.onPriceResolved === 'function') props.onPriceResolved();
      });
    }
    try {
      const _vc = JSON.parse(localStorage.getItem(_vKey) || 'null');
      if (_vc && Number.isFinite(_vc.total) && _vc.total > 0) {
        const _vRate = getExchangeRate(_vc.currency) || exchangeRate;
        const _vDisp = (_vc.total * (exchangeRate / _vRate)) / divider;
        // Override ONLY when the endpoint differs — same condition as every other channel.
        if (Number.isFinite(_vDisp) && _vDisp > 0 && Math.abs(_vDisp - finalTotalAmount) > 0.01) {
          finalTotalAmount = _vDisp;
          vibNet = _vc.total * (exchangeRate / _vRate);
        }
      }
    } catch (e) { /* endpoint override best-effort; local VIB price stands */ }

    const commissionAmount = commissionOfTotal(finalTotalAmount);

    dlog('VillasInStBarth final calculation:', { totalDays, totalPrice, finalTotalAmount, commissionAmount, exchangeRate });

    return {
        totalAmount: finalTotalAmount,
        totalTaxes: 0,
        security: 0,
        agencyCommission: commissionAmount,
        loadingPrice: false,
        currency: currency || "USD",
        isVillasInStBarth: true,
        basePrice: vibNet,
        totalDays
    };
}

  // dlog('currency:', currency, selectedCurrencyRate, divider)
  if (property && (!dateFrom || !dateTo)) { // means return PRICE PER NIGHT only from base price.
    totalAmount = noDatesBaseNightly() * multiplier;
    localStorage.removeItem('SH_PROP') //remove from local.
    localStorage.removeItem('totalExPrice')
  } else if (property && dateFrom && dateTo && fullCalendar && !isSH) {
    dlog('control is here')
    // Route this listing's price through the ONE unified endpoint (Guesty / VT-channel
    // / Invenio). Fire-and-store like HW/BP; the override just below applies the
    // endpoint's NET. Guesty's endpoint adapter (qouteGS) runs the same calendar math
    // computed here, so it's the identical number — sourced from the endpoint.
    const _gDF = dayjs(dateFrom).format('YYYY-MM-DD');
    const _gDT = dayjs(dateTo).format('YYYY-MM-DD');
    const _gGuests = (Number(adults) || 0) + (Number(children) || 0) || 1;
    const _gKey = `uniVerifiedPrice_${hubTag}_${propertyId}_${_gDF}_${_gDT}_${_gGuests}`;
    if (propertyId && !localStorage.getItem(_gKey)) {
      localStorage.setItem(_gKey, JSON.stringify({ pending: true }));
      axios.get(`${quoteHub}/api/booking/quote`, {
        params: { listingId: propertyId, checkIn: _gDF, checkOut: _gDT, guests: _gGuests, currency },
        headers: { 'x-api-key': constants.SHUB_API_KEY, Authorization: `Bearer ${constants.SHUB_STATIC_TOKEN}` },
      }).then((resp) => {
        const d = resp?.data || {};
        if (d.available && Number.isFinite(d.netTotal)) {
          localStorage.setItem(_gKey, JSON.stringify({ total: d.netTotal, currency: d.currency }));
        } else {
          localStorage.setItem(_gKey, JSON.stringify({ failed: true }));
        }
        if (typeof props?.onPriceResolved === 'function') props.onPriceResolved();
      }).catch(() => {
        localStorage.setItem(_gKey, JSON.stringify({ failed: true }));
        if (typeof props?.onPriceResolved === 'function') props.onPriceResolved();
      });
    }
    let notAvail = false
    let sum = 0;
    let currentDate = dayjs(dateFrom);
    const toDate = dayjs(dateTo);

    while (currentDate.isBefore(toDate, 'day')) {
      const formattedDate = currentDate.format('YYYY-MM-DD');
      const formattedNextDate = currentDate.add(1, 'day').format('YYYY-MM-DD');
      const selectedDay = fullCalendar ? fullCalendar
        .filter((period) => dayjs(period?.date).isSame(formattedDate, 'day')) : null

      dlog('day or res:', formattedDate, selectedDay)
      if (!selectedDay) {
        error.push({ error: 'could not find day in the fullCalendar: ' + formattedDate + ' id:' + property._id })
        return { error }
      }
      if (selectedDay && Array.isArray(selectedDay)) {
        //  dlog('sel:',selectedDay)

        // if (Object.prototype.hasOwnProperty.call(selectedDay[0], 'allotment')) {
        if (!selectedDay[0]?.allotment) {
          error.push({ error: 'day is not available: ' + formattedDate + ' id:' + property._id })
          notAvail = true
        }
      } else {
        //dlog('sel:',selectedDay)
        error.push({ error: 'could not find day allotment in the fullCalendar: ' + formattedDate })
        notAvail = true
      }

      const selectedPrice = selectedDay.map((period) => period.price);
      const selectedPriceCurrency = selectedDay.map((period) => period.currency)[0];
      if (selectedPrice) {
        dlog('currency compare', currency, selectedPriceCurrency)
        const targetCur = (currency || 'USD').toUpperCase();
        const dayCur = (selectedPriceCurrency || targetCur).toUpperCase();
        const dayPrice = Number(selectedPrice[0]) || 0;

        // keep `sum` ALWAYS in `targetCur`
        if (dayCur === targetCur) {
          sum += dayPrice;
        } else {
          const targetRate = getExchangeRate(targetCur);
          const dayRate = getExchangeRate(dayCur) || 1; // avoid divide-by-0
          const dayToTarget = targetRate / dayRate;

          sum += dayPrice * dayToTarget;
        }
        // sum += selectedPrice[0];
        addToDailyRates({
          price: dayPrice,
          EffectiveDate: formattedDate,
          ExpireDate: formattedNextDate
        }
        );
        ;
      } else {
        error.push({ error: 'day price is not available: ' + formattedDate })
        return { error }
      }
      currentDate = currentDate.add(1, 'day');
    }

    let extraGuests = accommodates - (property?.prices?.guestsIncludedInRegularFee || 0);
    if (extraGuests < 0) extraGuests = 0;
    const extraPersonFee = property?.prices?.extraPersonFee || 0;
    const extraGuestsTotalFee = extraGuests * extraPersonFee * nights;

    // extraGuestsTotalFee is in property currency -> convert to `currency` before adding to sum
    const targetCur = (currency || 'USD').toUpperCase();
    const propCur = (property?.prices?.currency || targetCur).toUpperCase();

    if (propCur === targetCur) {
      sum += extraGuestsTotalFee;
    } else {
      const targetRate = getExchangeRate(targetCur);
      const propRate = getExchangeRate(propCur) || 1; // avoid divide-by-0
      sum += (extraGuestsTotalFee * (targetRate / propRate));
    }
    // sum += extraGuestsTotalFee;

    if (nights >= 28 && property?.prices?.monthlyPriceFactor) {
      sum *= property?.prices?.monthlyPriceFactor;
    } else if (nights >= 7 && property?.prices?.weeklyPriceFactor) {
      sum *= property?.prices?.weeklyPriceFactor;
    }

    let totalTaxPrice = 0;
    let taxes = property?.taxes?.length > 0 ? property?.taxes : [];
    taxesArrayRES.length = 0
    taxesArray.length = 0
    // CF
    addToTaxes(
      {
        amount: sum,
        // amount: sum * multiplier,
        description: "Accomodation Fare",
        initial: "AF"
      }
    )
    if (property?.prices?.cleaningFee) {
      const cleaningFeeProp = Number(property?.prices?.cleaningFee) || 0;

      const targetCur = (currency || 'USD').toUpperCase();
      const propCur = (property?.prices?.currency || targetCur).toUpperCase();

      const cleaningFeeDisplay =
        propCur === targetCur
          ? cleaningFeeProp
          : cleaningFeeProp * ((getExchangeRate(targetCur) / (getExchangeRate(propCur) || 1)));

      totalTaxPrice += cleaningFeeDisplay; // IMPORTANT: totalTaxPrice is now in display currency

      addToTaxes({
        amount: cleaningFeeDisplay, // no multiplier here
        description: "Cleaning Fee",
        initial: "CF"
      });

      addToTaxesRES({
        amount: cleaningFeeProp, // keep property currency here
        description: "Cleaning Fee",
        initial: "CF"
      });
    }
    // if (property?.prices?.cleaningFee) {
    //   //setTaxesArray(prev => ({ ...prev, 'Cleaning Fee': property?.prices?.cleaningFee * multiplier }));
    //   totalTaxPrice = property?.prices?.cleaningFee
      
    //   addToTaxes(
    //     {
    //       amount: property?.prices?.cleaningFee * multiplier,
    //       description: "Cleaning Fee",
    //       initial: "CF"
    //     }
    //   )
    //   // on res
    //   addToTaxesRES(
    //     {
    //       amount: property?.prices?.cleaningFee,
    //       description: "Cleaning Fee",
    //       initial: "CF"
    //     }
    //   )
    // }
    //dlog('accomodates:', accommodates)
    // taxes.forEach((item) => {
    //   let taxAmount = 0;
    //   //dlog(item)
    //   if (isPercentage(item?.units)) {
    //     taxAmount = (sum * item?.amount) / 100;
    //   } else {
    //     switch (item.quantifier) {
    //       case 'PER_STAY':
    //         taxAmount = item.amount;
    //         break;
    //       case 'PER_GUEST':

    //         taxAmount = item.amount * accommodates;
    //         break;
    //       case 'PER_NIGHT':
    //         taxAmount = item.amount * nights;
    //         break;
    //       case 'PER_GUEST_PER_NIGHT':
    //         taxAmount = item.amount * accommodates * nights;
    //         break;
    //       default:
    //         break;
    //     }
    //   }
    //   //setTaxesArray(prev => ({ ...prev, [item.type]: taxAmount * multiplier }));
    //   addToTaxesRES(
    //     {
    //       amount: taxAmount * multiplier,
    //       description: getTaxPerType(item.type).description,
    //       initial: getTaxPerType(item.type).initial
    //     }
    //   )
    //   addToTaxes(
    //     {
    //       amount: taxAmount,
    //       description: getTaxPerType(item.type).description,
    //       initial: getTaxPerType(item.type).initial
    //     }
    //   );
    //   totalTaxPrice += taxAmount
    // });
   
    taxes.forEach((item) => {
      let taxAmount = 0;

      if (isPercentage(item?.units)) {
        taxAmount = (sum * item?.amount) / 100; // sum is DISPLAY currency
      } else {
        // item.amount is almost certainly stored in PROPERTY currency.
        // Convert item.amount into DISPLAY first, then apply quantifier math.
        const targetCur = (currency || 'USD').toUpperCase();
        const propCur = (property?.prices?.currency || targetCur).toUpperCase();

        const itemAmountDisplay =
          propCur === targetCur
            ? item.amount
            : item.amount * (getExchangeRate(targetCur) / (getExchangeRate(propCur) || 1));

        switch (item.quantifier) {
          case 'PER_STAY':
            taxAmount = itemAmountDisplay;
            break;
          case 'PER_GUEST':
            taxAmount = itemAmountDisplay * accommodates;
            break;
          case 'PER_NIGHT':
            taxAmount = itemAmountDisplay * nights;
            break;
          case 'PER_GUEST_PER_NIGHT':
            taxAmount = itemAmountDisplay * accommodates * nights;
            break;
          default:
            break;
        }
      }

      addToTaxes({
        amount: taxAmount, // DISPLAY
        description: getTaxPerType(item.type).description,
        initial: getTaxPerType(item.type).initial
      });

      addToTaxesRES({
        amount: taxAmount * (propertyExchangeRate / exchangeRate), // PROPERTY
        description: getTaxPerType(item.type).description,
        initial: getTaxPerType(item.type).initial
      });

      totalTaxPrice += taxAmount; // DISPLAY
    });
    if (property?.prices?.securityDepositFee) {
      const deposit = property?.prices?.securityDepositFee
      security = deposit * multiplier
      localStorage.setItem('security', deposit * multiplier)
      dlog('security:', deposit, property?.prices?.currency, 'in ', currency, deposit * multiplier)

      // addToTaxes(
      //   {
      //     amount: deposit * multiplier,
      //     description: "Security deposit (returned to client at end of stay)",
      //     initial: "SE"
      //   }
      // )
    }
    // pricing: generic-branch markup RE-ENABLED. Non-Guesty listings that hit this
    // branch (InvenioHomes, VT-channel Guesty, SH-uppercase ids) were being sold at
    // NET here (the `* multiplier` was commented out); now they get the same
    // net->selling markup (17%/22%) as every other branch. RES values stay net.
    totalAF = sum * multiplier;
    totalAFRES = sum * (propertyExchangeRate / exchangeRate);
    totalAmountRES = sum + totalTaxPrice;
    totalTaxes = totalTaxPrice * multiplier;
    dlog('total calculation',`(${sum} + ${totalTaxPrice}) * ${multiplier} = `,(sum + totalTaxPrice) * multiplier)
    totalAmount = (sum + totalTaxPrice) * multiplier;
    totalTaxesRES = totalTaxPrice * (propertyExchangeRate / exchangeRate);
    // totalAF = notAvail ? 0 : sum * multiplier;
    // totalAFRES = notAvail ? 0 : sum;
    // totalAmountRES = notAvail ? 0 : sum + totalTaxPrice;
    // totalTaxes = notAvail ? 0 : totalTaxPrice * multiplier;
    // totalAmount = notAvail ? 0 : (sum + totalTaxPrice) * multiplier;
    // totalTaxesRES = notAvail ? 0 : totalTaxPrice;

    addToTaxesRES(
      {
        amount: -(sum + totalTaxPrice) * COMMISSION_RATE_NET, // 10% of net (a reservation line, not the agent's commission)
        description: "Prededucted Host Channel Fee",
        initial: "PCM"
      }
    )
    // Source the price from the unified endpoint for Guesty/generic + Invenio:
    // compute local first, then override with the endpoint's NET ONLY when it
    // differs (same condition HW/BP/RU use — uniform flow across every channel).
    try {
      const _gc = JSON.parse(localStorage.getItem(_gKey) || 'null');
      if (_gc && Number.isFinite(_gc.total) && _gc.total > 0) {
        const _gRate = getExchangeRate(_gc.currency) || propertyExchangeRate;
        const _gDisp = (_gc.total * (exchangeRate / _gRate)) / divider;
        if (Number.isFinite(_gDisp) && _gDisp > 0 && Math.abs(_gDisp - totalAmount) > 0.01) {
          totalAmount = _gDisp;
          totalAmountRES = _gc.total * (propertyExchangeRate / _gRate);
        }
      }
    } catch (e) { /* endpoint override best-effort; local price stands */ }
  };

  calculated = true
 

  //resPayload = buildRESPayload()
  if (isSH && dateFrom && dateTo && localStorage.getItem("totalExPrice")) {
    totalAmount = localStorage.getItem("totalExPrice") ?
      parseInt(localStorage.getItem("totalExPrice")) * multiplier : 0
    totalTaxes = 0
    loadingPrice=false
    dlog('return exPrice!', loadingPrice, totalAmount)
  } else
  if (isSH && !externalPrice && dateFrom && dateTo&& !localStorage.getItem("totalExPrice")) { // if dates selected and SH and did not get price from API yet...
    if (!externalPrice) {
      loadingPrice=true
      getEXprice()
    } else {loadingPrice=false

    }
  } 

  
  

  agencyCommission = commissionOfTotal(totalAmount);
  agencyCommissionRES = commissionFromNet(totalAmountRES, divider);

  const price = {
    loadingPrice,
    currency,
    exchangeRate,
    propertyExchangeRate,
    //reservation_id,
    totalAmount,
    totalTaxes,
    taxesArray,
    agencyCommission,
  }

  const success = (!error)
  dlog('price return:', price)
  return {
    loadingPrice,
    error,
    calculated,
    success,
    reservation_id,
    totalAmount,
    security,
    totalTaxes,
    taxesArray,
    agencyCommission,
    totalAmountRES,
    totalTaxesRES,
    taxesArrayRES,
    agencyCommissionRES,
    exchangeRate,
    propertyExchangeRate,
    resPayload
  }
}

export default makeCalculations;
   